import { useCallback, useEffect, useState } from 'react';
import { api, ApiError, type InboundMailStatus } from '../api';
import { type Dictionary, type Locale } from '../i18n';
import * as Icon from '@firmeto/design/Icons';

/**
 * Odbiór dokumentów z poczty (E17-S2) — stan, nie konfiguracja.
 *
 * Domenę i regułę w Mailgunie API ustawia samo; ta strona mówi, czy to się udało i czy w DNS są
 * rekordy MX. Gdy ich brakuje, pokazuje dokładnie, co wpisać u dostawcy DNS, a „Sprawdź teraz”
 * pozwala potwierdzić bez czekania na restart albo poranną kontrolę.
 */
export function InboundMail({ t, locale }: { t: Dictionary; locale: Locale }) {
  const [status, setStatus] = useState<InboundMailStatus | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setStatus(await api.inboundMail());
    } catch (failure) {
      setError(failure instanceof ApiError ? failure.message : String(failure));
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  async function check() {
    setBusy(true);
    setError(null);
    try {
      setStatus(await api.checkInboundMail());
    } catch (failure) {
      setError(failure instanceof ApiError ? failure.message : String(failure));
    } finally {
      setBusy(false);
    }
  }

  const checkedAt = status?.checkedAt
    ? new Date(status.checkedAt).toLocaleString(locale === 'pl' ? 'pl-PL' : 'en-GB', { dateStyle: 'medium', timeStyle: 'short' })
    : t.inboundMail.never;

  return (
    <>
      <div className="hd">
        <div>
          <h3>{t.inboundMail.title}</h3>
          <p>{t.inboundMail.lead}</p>
        </div>
        {status?.configured && (
          <button className="btn" type="button" disabled={busy} onClick={() => void check()}>
            {busy ? t.inboundMail.checking : t.inboundMail.check}
          </button>
        )}
      </div>

      {error && (
        <div className="alert a-bad">
          <Icon.Error />
          <div>
            <b>{error}</b>
          </div>
        </div>
      )}

      {status === null && !error && <p className="muted">{t.loading}</p>}

      {status && !status.configured && (
        <div className="alert a-warn">
          <Icon.Warn />
          <div>
            <b>{t.inboundMail.notConfigured}</b>
          </div>
        </div>
      )}

      {status && status.configured && (
        <div className={`alert ${status.ready ? 'a-ok' : 'a-warn'}`}>
          {status.ready ? <Icon.Check /> : <Icon.Warn />}
          <div>
            <b>{status.ready ? t.inboundMail.stateReady : t.inboundMail.stateWaiting}</b>
          </div>
        </div>
      )}

      {status && (
        <div className="grid two">
          <div className="panel">
            <dl className="kv">
              <dt>{t.inboundMail.domain}</dt>
              <dd className="mono">{status.domain}</dd>
              <dt>{t.inboundMail.webhook}</dt>
              <dd className="mono">{status.webhookUrl}</dd>
              <dt>{t.inboundMail.checkedAt}</dt>
              <dd>{checkedAt}</dd>
            </dl>
          </div>

          {status.configured && (
            <div className="panel">
              <table className="tbl">
                <tbody>
                  <Row label={t.inboundMail.domainKnown} ok={status.domainKnown} t={t} />
                  <Row label={t.inboundMail.routeReady} ok={status.routeReady} t={t} />
                  <Row label={t.inboundMail.mxValid} ok={status.mxValid} t={t} />
                </tbody>
              </table>
              {status.problem && (
                <p className="hint">
                  {t.inboundMail.problem}: <b>{status.problem}</b>
                </p>
              )}
            </div>
          )}
        </div>
      )}

      {status && status.configured && status.domainKnown && !status.mxValid && (
        <div className="panel">
          <div className="hd">
            <div>
              <h4>{t.inboundMail.records}</h4>
              <p>{t.inboundMail.recordsHint}</p>
            </div>
          </div>
          <table className="tbl">
            <thead>
              <tr>
                <th>{t.inboundMail.recordType}</th>
                <th>{t.inboundMail.recordPriority}</th>
                <th>{t.inboundMail.recordValue}</th>
                <th>{t.inboundMail.recordState}</th>
              </tr>
            </thead>
            <tbody>
              {status.dnsRecords.map((record) => (
                <tr key={`${record.type}-${record.value}`}>
                  <td className="mono">{record.type}</td>
                  <td className="mono">{record.priority ?? '—'}</td>
                  <td className="mono">{record.value}</td>
                  <td>
                    <span className={`chip ${record.valid ? 'st-ok' : 'st-bad'}`}>{record.valid ? t.inboundMail.valid : t.inboundMail.invalid}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}

function Row({ label, ok, t }: { label: string; ok: boolean; t: Dictionary }) {
  return (
    <tr>
      <td>{label}</td>
      <td>
        <span className={`chip ${ok ? 'st-ok' : 'st-bad'}`}>{ok ? t.inboundMail.yes : t.inboundMail.no}</span>
      </td>
    </tr>
  );
}
